import { loadGoogleMapsPlaceSDK } from "@/lib/loadGoogleMaps";

export interface CitySuggestion {
  placeId: string;
  label: string;
}

export interface CityLocation {
  name: string;
  lat: number;
  lng: number;
}

type PlacesNamespace = {
  AutocompleteSuggestion?: {
    fetchAutocompleteSuggestions: (req: unknown) => Promise<{ suggestions: unknown[] }>;
  };
  AutocompleteSessionToken?: new () => unknown;
  AutocompleteService?: new () => {
    getPlacePredictions: (
      req: { input: string; types: string[] },
      cb: (predictions: unknown[] | null, status: string) => void
    ) => void;
  };
  PlacesServiceStatus?: Record<string, string>;
  Place?: new (opts: { id: string }) => {
    fetchFields: (opts: { fields: string[] }) => Promise<void>;
    displayName?: string;
    location?: { lat: () => number; lng: () => number } | { lat: number; lng: number };
  };
  PlacesService?: new (el: HTMLElement) => {
    getDetails: (
      req: { placeId: string; fields: string[] },
      cb: (place: unknown, status: string) => void
    ) => void;
  };
};

function getPlaces(): PlacesNamespace | null {
  return (
    (window as unknown as { google?: { maps?: { places?: PlacesNamespace } } }).google?.maps
      ?.places ?? null
  );
}

/** City-only autocomplete (types: ["(cities)"]) for the /chat page's city picker. */
export async function searchCityAutocomplete(query: string): Promise<CitySuggestion[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];

  await loadGoogleMapsPlaceSDK();
  const g = getPlaces();
  if (!g) return [];

  if (g.AutocompleteSuggestion) {
    try {
      const { suggestions } = await g.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: trimmed,
        includedPrimaryTypes: ["locality"],
      });
      return (suggestions as Array<{ placePrediction?: { placeId: string; text?: { toString: () => string } } }>)
        .filter((s) => s.placePrediction)
        .map((s) => ({
          placeId: s.placePrediction!.placeId,
          label: s.placePrediction!.text?.toString() ?? "",
        }));
    } catch {
      /* legacy fallback below */
    }
  }

  if (!g.AutocompleteService) return [];

  return new Promise((resolve) => {
    const svc = new g.AutocompleteService!();
    svc.getPlacePredictions({ input: trimmed, types: ["(cities)"] }, (predictions, status) => {
      const ok = g.PlacesServiceStatus?.OK ?? "OK";
      if (status !== ok || !predictions) {
        resolve([]);
        return;
      }
      resolve(
        (predictions as Array<{ place_id: string; description: string }>).map((p) => ({
          placeId: p.place_id,
          label: p.description,
        }))
      );
    });
  });
}

/** Resolves a city suggestion's placeId into a name + coordinates for map centering. */
export async function fetchCityLocation(placeId: string): Promise<CityLocation | null> {
  if (!placeId.trim()) return null;

  await loadGoogleMapsPlaceSDK();
  const g = getPlaces();
  if (!g) return null;

  if (g.Place) {
    try {
      const place = new g.Place({ id: placeId });
      await place.fetchFields({ fields: ["id", "displayName", "location"] });
      const loc = place.location;
      if (!loc || !place.displayName) return null;
      const lat = typeof loc.lat === "function" ? loc.lat() : loc.lat;
      const lng = typeof loc.lng === "function" ? loc.lng() : loc.lng;
      return { name: place.displayName, lat, lng };
    } catch {
      /* legacy fallback below */
    }
  }

  if (!g.PlacesService) return null;

  return new Promise((resolve) => {
    const svc = new g.PlacesService!(document.createElement("div"));
    svc.getDetails({ placeId, fields: ["name", "geometry"] }, (place, status) => {
      const ok = g.PlacesServiceStatus?.OK ?? "OK";
      const p = place as { name?: string; geometry?: { location?: { lat: () => number; lng: () => number } } } | null;
      if (status !== ok || !p?.geometry?.location || !p.name) {
        resolve(null);
        return;
      }
      resolve({ name: p.name, lat: p.geometry.location.lat(), lng: p.geometry.location.lng() });
    });
  });
}

/** Reverse-geocodes coordinates (e.g. from browser geolocation) into a city name. */
export async function reverseGeocodeCity(lat: number, lng: number): Promise<string | null> {
  await loadGoogleMapsPlaceSDK();
  const google = (window as unknown as { google?: typeof globalThis.google }).google;
  if (!google?.maps?.Geocoder) return null;

  const geocoder = new google.maps.Geocoder();
  try {
    const { results } = await geocoder.geocode({ location: { lat, lng } });
    for (const result of results) {
      const locality = result.address_components.find((c) => c.types.includes("locality"));
      if (locality) return locality.long_name;
    }
    return results[0]?.formatted_address ?? null;
  } catch {
    return null;
  }
}
